import api from "./api";

/**
 * Normalizes chart context into a standardized payload for the backend AI Analyzer API.
 * Also builds an `additional_context` block so the LLM receives rich, human-readable context
 * (machine names, aggregation granularity, etc.) for more specific, varied analysis.
 *
 * @param {object} options
 * @param {string}  options.chartTitle    - Display title shown on the chart
 * @param {string}  options.metricName    - Metric type (Power, Voltage, PF, THD, etc.)
 * @param {string}  options.unit          - Unit label (kW, V, %, A, etc.)
 * @param {Array}   options.timestamps    - Array of timestamp strings
 * @param {Array}   options.datasets      - Array of { label, data[] } objects
 * @param {object}  options.dateRange     - { start, end } date strings
 * @param {string}  [options.aggregation] - Aggregation granularity (e.g. "Hourly", "Daily", "15-min")
 * @param {string}  [options.tabContext]  - Which chart/section this is from (e.g. "Historical Energy")
 */
export function buildChartPayload({
  chartTitle = "Chart Analysis",
  metricName = "Power",
  unit = "",
  timestamps = [],
  datasets = [],
  dateRange = null,
  aggregation = null,
  tabContext = null,
}) {
  // Ensure timestamps are formatted as strings
  const formattedTimestamps = (timestamps || []).map((t) => String(t));

  // Ensure datasets are normalized and labels are clean
  const normalizedDatasets = (datasets || []).map((ds) => {
    let cleanLabel = ds.label || "Series";
    // Strip metric unit parenthesis from label if present
    cleanLabel = cleanLabel.replace(/\s*\([^)]*\)\s*$/, "").trim();

    return {
      label: cleanLabel,
      data: Array.isArray(ds.data)
        ? ds.data.map((val) => (val == null || !Number.isFinite(Number(val)) ? null : Number(val)))
        : [],
    };
  });

  // Extract unique machine/series names for the LLM to reference by name
  const machineNames = normalizedDatasets
    .map((ds) => ds.label)
    .filter(Boolean)
    .filter((v, i, a) => a.indexOf(v) === i);

  // Build additional context block — all fields are optional but richer = better LLM output
  const additionalContext = {};
  if (machineNames.length > 0) {
    additionalContext["Monitored machines / series"] = machineNames.join(", ");
  }
  if (aggregation) {
    additionalContext["Data aggregation"] = aggregation;
  }
  if (tabContext) {
    additionalContext["Chart section"] = tabContext;
  }
  if (dateRange?.start && dateRange?.end) {
    additionalContext["Selected window"] = `${dateRange.start} to ${dateRange.end}`;
  }
  if (normalizedDatasets.length > 1) {
    additionalContext["Number of series"] = String(normalizedDatasets.length);
  }

  return {
    chart_title: chartTitle,
    metric_name: metricName,
    unit: unit || "",
    timestamps: formattedTimestamps,
    datasets: normalizedDatasets,
    date_range: dateRange
      ? {
          start: dateRange.start ? String(dateRange.start) : null,
          end: dateRange.end ? String(dateRange.end) : null,
        }
      : null,
    additional_context: Object.keys(additionalContext).length > 0 ? additionalContext : null,
  };
}

/**
 * Invokes the backend LLM chart analyzer endpoint.
 */
export async function requestChartAIAnalysis(chartPayload) {
  try {
    const response = await api.post("/analyze-chart", chartPayload);
    if (response.data && response.data.status === "success") {
      return response.data.analysis;
    }
  } catch (error) {
    console.warn("API AI Analysis request error, relying on local synthesis:", error);
  }

  // Fallback client-side statistical engine if API is unreachable
  return computeLocalFallback(chartPayload);
}

function computeLocalFallback(payload) {
  const allValues = [];
  (payload.datasets || []).forEach((ds) => {
    (ds.data || []).forEach((v) => {
      if (v != null && Number.isFinite(v)) allValues.push(v);
    });
  });

  if (!allValues.length) {
    return {
      overall_trend: "Insufficient chart data available in the selected window for trend synthesis.",
      possible_causes: ["No recorded data points in the selected range."],
      recommended_actions: ["Select a broader date range or verify meter connectivity."],
      concise_summary: "No data available for analysis.",
      stats: { min: 0, max: 0, avg: 0, peak_time: "N/A", trend_direction: "Stable" },
    };
  }

  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const avg = allValues.reduce((a, b) => a + b, 0) / allValues.length;
  const start = allValues[0];
  const end = allValues[allValues.length - 1];

  let trend = "Stable";
  const diff = end - start;
  if (Math.abs(diff / (start || 1)) > 0.05) {
    trend = diff > 0 ? "Increasing" : "Decreasing";
  }

  const unit = payload.unit || "";
  const title = payload.chart_title || "Chart";

  return {
    overall_trend: `The ${title} exhibited a ${trend.toLowerCase()} pattern, with values ranging between ${min.toFixed(2)} ${unit} and a peak of ${max.toFixed(2)} ${unit} (average: ${avg.toFixed(2)} ${unit}).`,
    possible_causes: [
      "Operational workload shifts across facility shifts.",
      "Equipment startup load cycles during active operating windows.",
      "Variations in ambient conditions and production schedules."
    ],
    recommended_actions: [
      "Stagger high-load equipment starts to avoid peak demand surges.",
      "Monitor baseline power quality metrics during peak operating hours.",
      "Ensure preventive maintenance schedules are strictly observed."
    ],
    concise_summary: `${title} averaged ${avg.toFixed(2)} ${unit} across the selected window with a peak of ${max.toFixed(2)} ${unit}. System operating parameters remain stable.`,
    stats: {
      min: Number(min.toFixed(2)),
      max: Number(max.toFixed(2)),
      avg: Number(avg.toFixed(2)),
      peak_time: "Peak window",
      trend_direction: trend,
    },
  };
}
